import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';

import { DialpadPopover } from '@/components/DialpadPopover/DialpadPopover';
import { useSoftPhone } from '@/contexts/softPhoneContext';

import type { Props } from './types';

/**
 * The phone icon and its dialpad popover, shared by the mobile top bar and the desktop sidebar.
 *
 * Renders nothing when the user has no softphone permission, and a disabled icon when the
 * softphone is unavailable because we are embedded.
 */
const DialpadButton = ({
  wrapperClassName = '',
  className = 'hover:text-gray-700',
  popoverClassName = 'absolute right-0 top-full mt-1 z-40',
  label
}: Props) => {
  const softPhone = useSoftPhone();
  const [showDialpad, setShowDialpad] = useState(false);
  const dialpadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDialpad) return;

    const handler = (e: MouseEvent) => {
      if (dialpadRef.current && !dialpadRef.current.contains(e.target as Node)) {
        setShowDialpad(false);
      }
    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  }, [showDialpad]);

  if (softPhone === null) return null;

  if (softPhone === 'disabled-in-embed') {
    return (
      <div className={wrapperClassName}>
        <button
          type="button"
          disabled
          className={`${className} cursor-not-allowed opacity-40`}
          aria-label="Phone dialer unavailable"
          title="Phone dialer is not supported in this view">
          <FontAwesomeIcon icon={faPhone} />
          {label && <span>{label}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${wrapperClassName}`} ref={dialpadRef}>
      <button
        type="button"
        onClick={() => setShowDialpad((prev) => !prev)}
        className={className}
        aria-label="Open dialpad"
        title="Dial a number">
        <FontAwesomeIcon icon={faPhone} />
        {label && <span>{label}</span>}
      </button>

      {showDialpad && (
        <div className={popoverClassName}>
          <DialpadPopover onClose={() => setShowDialpad(false)} />
        </div>
      )}
    </div>
  );
};

export default React.memo(DialpadButton);
